import { useEffect, useMemo, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Clock,
  Droplets,
  Heart,
  Leaf,
  MapPin,
  Menu,
  Phone,
  Sparkles,
  Star,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const media = {
  hero:
    "https://static.prod-images.emergentagent.com/jobs/247de90e-ea95-40bd-bf96-3782d264a03e/images/cd232b806ad05529d9ccd324ce383892be5495946aa07775aea046bf8e0df43a.png",
  about: "https://images.pexels.com/photos/8215110/pexels-photo-8215110.jpeg",
  splash:
    "https://static.prod-images.emergentagent.com/jobs/247de90e-ea95-40bd-bf96-3782d264a03e/images/953416f421d78397638ad5a2f2dcda25e9c15cb37ccea7a65316a4403e8b9836.png",
  abstract:
    "https://static.prod-images.emergentagent.com/jobs/247de90e-ea95-40bd-bf96-3782d264a03e/images/f3c83793bf0fb318197e2705b0e0db81093e1b1b08b2ee373bffb8b58b4b831f.png",
  gallery: [
    "https://images.pexels.com/photos/4239/restaurant-drink-colorful-colourful.jpg",
    "https://images.pexels.com/photos/18095475/pexels-photo-18095475.jpeg",
    "https://images.pexels.com/photos/7683771/pexels-photo-7683771.jpeg",
  ],
};

const fallbackBusiness = {
  name: "Lifestyle Juices",
  tagline: "Fresh, made-to-order juices and smoothies in Des Moines.",
  address: "2418 University Ave, Des Moines, IA 50311",
  phone: "(515) 777-2301",
  hours_note: "Official hours: Monday–Friday 10 AM–7 PM, Saturday–Sunday 10 AM–5 PM.",
  weekly_hours: [
    { days: "Monday–Friday", hours: "10 AM–7 PM" },
    { days: "Saturday", hours: "10 AM–5 PM" },
    { days: "Sunday", hours: "10 AM–5 PM" },
  ],
  ordering_url: "https://www.doordash.com/store/lifestyle-juices-des-moines-24681011/",
  offerings: [
    "Fresh juices",
    "Smoothies",
    "Wellness shots",
    "Mostly vegan options",
    "Made-to-order drinks",
  ],
  menu: [
    {
      name: "Sweet Emotions",
      description: "A most-ordered DoorDash favorite with a bright, smooth fruit-forward profile.",
      category: "Original Smoothies",
      accent: "Featured",
      price: "$8.49",
      source: "DoorDash",
    },
    {
      name: "Irie",
      description: "A guest-favorite smoothie listed on DoorDash and praised in public reviews.",
      category: "Original Smoothies",
      accent: "Tropical",
      price: "$11.49",
      source: "DoorDash",
    },
    {
      name: "Bahamian Blast",
      description: "A sunny island-style smoothie selection with bold fruit energy.",
      category: "Original Smoothies",
      accent: "Island",
      price: "$8.49",
      source: "DoorDash",
    },
    {
      name: "Fruit Passion",
      description: "A colorful DoorDash-listed smoothie with a lively fruit blend feel.",
      category: "Original Smoothies",
      accent: "Fruity",
      price: "$8.49",
      source: "DoorDash",
    },
    {
      name: "Citrus Roots",
      description: "A citrus-forward smoothie listed publicly on DoorDash.",
      category: "Original Smoothies",
      accent: "Citrus",
      price: "$8.49",
      source: "DoorDash",
    },
    {
      name: "Bright Eyes",
      description: "An original smoothie name found in public Lifestyle Juices menu listings.",
      category: "Original Smoothies",
      accent: "Fresh",
      price: "$8.49+",
      source: "Public listing",
    },
    {
      name: "Summer Mix",
      description: "A bright, refreshing blend listed by customers on public ordering platforms.",
      category: "Original Smoothies",
      accent: "Seasonal",
      source: "Public listing",
    },
    {
      name: "D.M.I.",
      description: "A Lifestyle Juices menu name seen in public customer order history.",
      category: "Original Smoothies",
      accent: "Local",
      source: "Public listing",
    },
    {
      name: "Sweet Greens",
      description: "A most-ordered green smoothie on DoorDash, loved for balanced fresh flavor.",
      category: "Green Smoothies",
      accent: "Greens",
      price: "$8.49",
      source: "DoorDash",
    },
    {
      name: "Marley Smoothie",
      description: "A must-try smoothie mentioned in public menu summaries.",
      category: "Original Smoothies",
      accent: "Favorite",
      source: "Public listing",
    },
    {
      name: "Fire",
      description: "A bold protein smoothie listed on DoorDash and public ordering menus.",
      category: "Protein Smoothies",
      accent: "Bold",
      price: "$11.49",
      source: "DoorDash",
    },
    {
      name: "Burner",
      description: "A protein smoothie name found on public Lifestyle Juices ordering listings.",
      category: "Protein Smoothies",
      accent: "Protein",
      price: "$8.49+",
      source: "Public listing",
    },
    {
      name: "Hulk Juice",
      description: "A nutrient-forward green/protein style drink noted in public menu summaries.",
      category: "Protein Smoothies",
      accent: "Power",
      price: "$8.49+",
      source: "Public listing",
    },
    {
      name: "Massive",
      description: "A protein smoothie name found on public Lifestyle Juices ordering listings.",
      category: "Protein Smoothies",
      accent: "Protein",
      price: "$8.49+",
      source: "Public listing",
    },
    {
      name: "Ginger Shot",
      description: "A zesty wellness shot for a quick, bold boost.",
      category: "Shots",
      accent: "Ginger",
      source: "Public listing",
    },
    {
      name: "Sea Moss Drink",
      description: "A wellness-focused drink noted by public menu sources and regulars.",
      category: "Wellness Drinks",
      accent: "Mineral",
    },
  ],
};

const navItems = [
  { label: "Menu", href: "#menu" },
  { label: "About", href: "#about" },
  { label: "Reviews", href: "#reviews" },
  { label: "Visit", href: "#visit" },
];

const menuCategories = [
  {
    title: "Fresh Juices",
    copy: "Made-to-order blends crafted for clean, fruit-forward flavor.",
    icon: Droplets,
    color: "bg-[#FF6B35]",
  },
  {
    title: "Smoothies",
    copy: "Creamy, colorful drinks with tropical energy and feel-good ingredients.",
    icon: Heart,
    color: "bg-[#FF4481]",
  },
  {
    title: "Wellness Shots",
    copy: "Small but mighty sips for bold, bright, on-the-go refreshment.",
    icon: Sparkles,
    color: "bg-[#96D035]",
  },
];

const testimonials = [
  {
    quote: "Fresh juice made to order, vibrant flavors, and a friendly local feel.",
    name: "Happy guest note",
  },
  {
    quote: "A colorful stop for smoothies, wellness drinks, and mostly vegan options.",
    name: "Community favorite",
  },
  {
    quote: "Exactly the kind of bright, refreshing drink you want near University Ave.",
    name: "Des Moines regular",
  },
];

function Header({ business }) {
  const [open, setOpen] = useState(false);

  const closeMenu = () => setOpen(false);

  return (
    <header data-testid="site-header" className="fixed inset-x-0 top-0 z-50 px-4 py-4 md:px-8">
      <nav
        data-testid="main-navigation"
        className="mx-auto flex max-w-7xl items-center justify-between rounded-full border border-black/5 bg-white/75 px-4 py-3 shadow-xl shadow-[#FF6B35]/10 backdrop-blur-xl md:px-6"
      >
        <a
          data-testid="brand-home-link"
          href="#home"
          className="flex items-center gap-3 rounded-full focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
          onClick={closeMenu}
        >
          <span className="grid h-11 w-11 place-items-center rounded-full bg-[#96D035] text-[#11221D] shadow-lg shadow-[#96D035]/30">
            <Leaf className="h-5 w-5" aria-hidden="true" />
          </span>
          <span data-testid="brand-name" className="font-heading text-xl font-semibold text-[#11221D]">
            {business.name}
          </span>
        </a>

        <div data-testid="desktop-navigation-links" className="hidden items-center gap-2 md:flex">
          {navItems.map((item) => (
            <a
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              key={item.href}
              href={item.href}
              className="rounded-full px-4 py-2 text-sm font-bold text-[#11221D]/75 transition-colors hover:bg-[#F3F7F2] hover:text-[#11221D] focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
            >
              {item.label}
            </a>
          ))}
        </div>

        <div className="hidden items-center gap-3 md:flex">
          <Button
            data-testid="header-call-button"
            asChild
            className="rounded-full bg-[#FF6B35] px-5 py-5 text-white shadow-lg shadow-[#FF6B35]/25 transition-[transform,background-color,box-shadow] hover:-translate-y-1 hover:bg-[#FF8A5D]"
          >
            <a href={`tel:${business.phone.replace(/[^0-9]/g, "")}`}>
              <Phone className="h-4 w-4" aria-hidden="true" /> Call Now
            </a>
          </Button>
        </div>

        <button
          data-testid="mobile-menu-toggle-button"
          type="button"
          aria-label="Toggle menu"
          onClick={() => setOpen((value) => !value)}
          className="grid h-11 w-11 place-items-center rounded-full bg-[#11221D] text-white transition-transform hover:-translate-y-1 md:hidden"
        >
          {open ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
        </button>
      </nav>

      {open && (
        <div
          data-testid="mobile-navigation-menu"
          className="mx-auto mt-3 max-w-7xl rounded-[2rem] border border-black/5 bg-white/95 p-4 shadow-xl shadow-[#11221D]/10 backdrop-blur-xl md:hidden"
        >
          {navItems.map((item) => (
            <a
              data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
              key={item.href}
              href={item.href}
              onClick={closeMenu}
              className="block rounded-2xl px-4 py-3 font-bold text-[#11221D] hover:bg-[#F3F7F2]"
            >
              {item.label}
            </a>
          ))}
          <a
            data-testid="mobile-call-link"
            href={`tel:${business.phone.replace(/[^0-9]/g, "")}`}
            className="mt-2 flex items-center justify-center gap-2 rounded-full bg-[#FF6B35] px-5 py-3 font-bold text-white"
          >
            <Phone className="h-4 w-4" aria-hidden="true" /> Call Now
          </a>
        </div>
      )}
    </header>
  );
}

function Hero({ business }) {
  return (
    <section id="home" data-testid="hero-section" className="relative overflow-hidden pt-28 md:pt-32">
      <div className="absolute -left-24 top-32 h-72 w-72 rounded-full bg-[#96D035]/30 blur-3xl" aria-hidden="true" />
      <div className="absolute right-0 top-0 h-96 w-96 rounded-full bg-[#FF4481]/20 blur-3xl" aria-hidden="true" />
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-16 md:px-12 lg:grid-cols-[1.02fr_0.98fr] lg:px-24 lg:py-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative z-10"
        >
          <Badge
            data-testid="hero-overline"
            className="mb-6 rounded-full border-0 bg-[#FFF0E9] px-4 py-2 text-[#FF6B35] shadow-none"
          >
            Fresh • Tropical • Made to order
          </Badge>
          <h1
            data-testid="hero-title"
            className="font-heading text-4xl font-semibold tracking-tight text-[#11221D] sm:text-5xl lg:text-6xl"
          >
            Juice that fits your lifestyle — bright, fresh, and full of flavor.
          </h1>
          <p data-testid="hero-subtitle" className="mt-6 max-w-2xl text-base leading-relaxed text-[#11221D]/75 md:text-lg">
            {business.tagline} Stop by for fresh juices, smoothies, wellness shots, and mostly vegan-friendly refreshment.
          </p>
          <div className="mt-9 flex flex-col gap-4 sm:flex-row">
            <Button
              data-testid="hero-visit-button"
              asChild
              className="rounded-full bg-[#FF6B35] px-7 py-6 text-base font-bold text-white shadow-xl shadow-[#FF6B35]/25 transition-[transform,background-color,box-shadow] hover:-translate-y-1 hover:bg-[#FF8A5D]"
            >
              <a href="#visit">
                Visit Us <ArrowRight className="h-5 w-5" aria-hidden="true" />
              </a>
            </Button>
            <Button
              data-testid="hero-call-button"
              asChild
              variant="outline"
              className="rounded-full border-[#11221D]/15 bg-white px-7 py-6 text-base font-bold text-[#11221D] shadow-lg shadow-[#11221D]/5 transition-[transform,background-color] hover:-translate-y-1 hover:bg-[#F3F7F2]"
            >
              <a href={`tel:${business.phone.replace(/[^0-9]/g, "")}`}>
                <Phone className="h-5 w-5" aria-hidden="true" /> {business.phone}
              </a>
            </Button>
          </div>
          <div data-testid="hero-quick-info" className="mt-10 grid gap-3 sm:grid-cols-2">
            <div className="rounded-[1.5rem] bg-white/80 p-4 shadow-lg shadow-[#FF6B35]/5 backdrop-blur">
              <MapPin className="mb-2 h-5 w-5 text-[#FF6B35]" aria-hidden="true" />
              <p data-testid="hero-address" className="font-bold text-[#11221D]">
                {business.address}
              </p>
              <p data-testid="menu-source-note" className="mt-3 text-sm leading-relaxed text-[#11221D]/60">
                DoorDash exposes featured drinks publicly; additional items are included from public menu listings where visible.
              </p>
            </div>
            <div className="rounded-[1.5rem] bg-white/80 p-4 shadow-lg shadow-[#96D035]/10 backdrop-blur">
              <Clock className="mb-2 h-5 w-5 text-[#96D035]" aria-hidden="true" />
                <p data-testid="hero-hours-note" className="font-bold text-[#11221D]">
                Mon–Fri 10–7 • Sat–Sun 10–5
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="relative"
        >
          <div data-testid="hero-image-card" className="relative overflow-hidden rounded-[2.5rem] bg-[#FFE8D8] p-4 shadow-2xl shadow-[#FF6B35]/20">
            <img
              data-testid="hero-fruit-image"
              src={media.hero}
              alt="Tropical fruits and fresh juice ingredients"
              className="aspect-[4/5] w-full rounded-[2rem] object-cover object-center"
            />
            <div
              data-testid="hero-floating-card"
              className="absolute bottom-7 left-7 right-7 rounded-[1.7rem] bg-white/85 p-5 shadow-xl backdrop-blur-xl"
            >
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF4481]">Local juice bar</p>
              <p className="mt-2 font-heading text-2xl font-semibold text-[#11221D]">Made fresh on University Ave</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function MenuPreview({ business }) {
  const grouped = useMemo(() => {
    return business.menu.reduce((acc, item) => {
      acc[item.category] = [...(acc[item.category] || []), item];
      return acc;
    }, {});
  }, [business.menu]);

  return (
    <section id="menu" data-testid="menu-section" className="px-6 py-20 md:px-12 lg:px-24 lg:py-32">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-10 lg:grid-cols-[0.82fr_1.18fr] lg:items-end">
          <div>
            <p data-testid="menu-overline" className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF6B35]">
              Menu highlights
            </p>
            <h2 data-testid="menu-title" className="mt-4 font-heading text-3xl font-semibold tracking-tight text-[#11221D] sm:text-4xl">
              From citrusy sips to creamy smoothies.
            </h2>
          </div>
          <p data-testid="menu-description" className="max-w-3xl text-base leading-relaxed text-[#11221D]/75 md:text-lg">
            Public listings describe Lifestyle Juices as a fresh, made-to-order juice bar. Exact public pricing is limited, so only confirmed pricing is shown.
          </p>
        </div>

        <div data-testid="menu-category-grid" className="mt-12 grid gap-6 md:grid-cols-3">
          {menuCategories.map((category) => {
            const Icon = category.icon;
            return (
              <Card
                data-testid={`menu-category-card-${category.title.toLowerCase().replaceAll(" ", "-")}`}
                key={category.title}
                className="group rounded-[2rem] border-[#E5EBE2] bg-white p-2 shadow-xl shadow-[#FF6B35]/5 transition-transform hover:-translate-y-2"
              >
                <CardContent className="p-6">
                  <div className={`mb-6 grid h-14 w-14 place-items-center rounded-2xl ${category.color} text-white shadow-lg`}>
                    <Icon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <h3 data-testid={`menu-category-title-${category.title.toLowerCase().replaceAll(" ", "-")}`} className="font-heading text-2xl font-semibold text-[#11221D]">
                    {category.title}
                  </h3>
                  <p data-testid={`menu-category-copy-${category.title.toLowerCase().replaceAll(" ", "-")}`} className="mt-3 text-[#11221D]/70">
                    {category.copy}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div data-testid="full-menu-preview" className="mt-10 grid gap-6 lg:grid-cols-2">
          {Object.entries(grouped).map(([category, items]) => (
            <div key={category} className="rounded-[2rem] bg-[#F3F7F2] p-6 md:p-8">
              <h3 data-testid={`full-menu-category-${category.toLowerCase().replaceAll(" ", "-")}`} className="font-heading text-2xl font-semibold text-[#11221D]">
                {category}
              </h3>
              <div className="mt-5 space-y-4">
                {items.map((item) => (
                  <div
                    data-testid={`menu-item-${item.name.toLowerCase().replaceAll(" ", "-")}`}
                    key={item.name}
                    className="rounded-[1.5rem] bg-white p-5 shadow-lg shadow-[#11221D]/5"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <h4 className="font-heading text-xl font-semibold text-[#11221D]">{item.name}</h4>
                      <span className="rounded-full bg-[#FFF0E9] px-3 py-1 text-xs font-bold text-[#FF6B35]">
                        {item.price ? `${item.price} • ${item.accent}` : item.accent}
                      </span>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-[#11221D]/70">{item.description}</p>
                    {item.source && (
                      <p
                        data-testid={`menu-item-source-${item.name.toLowerCase().replaceAll(" ", "-").replaceAll(".", "")}`}
                        className="mt-3 text-xs font-bold uppercase tracking-[0.16em] text-[#11221D]/40"
                      >
                        Source: {item.source}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div data-testid="ordering-note" className="mt-8 flex flex-col items-start justify-between gap-4 rounded-[2rem] bg-[#11221D] p-6 text-white md:flex-row md:items-center md:p-8">
          <div>
            <h3 className="font-heading text-2xl font-semibold">Order on DoorDash or visit in person.</h3>
            <p className="mt-2 text-white/70">Prices on delivery platforms may vary and are set by the merchant.</p>
          </div>
          <Button
            data-testid="future-ordering-button"
            asChild
            className="rounded-full bg-white px-6 py-5 text-[#11221D] transition-transform hover:-translate-y-1 hover:bg-[#F3F7F2]"
          >
            <a href={business.ordering_url} target="_blank" rel="noreferrer">
              View DoorDash menu <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}

function AboutSection({ business }) {
  return (
    <section id="about" data-testid="about-section" className="overflow-hidden bg-[#FFF8EF] px-6 py-20 md:px-12 lg:px-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[1fr_0.9fr] lg:items-center">
        <div className="relative">
          <img
            data-testid="about-juice-image"
            src={media.about}
            alt="Colorful fresh juices on a counter"
            className="aspect-[5/4] w-full rounded-[2.5rem] object-cover object-center shadow-2xl shadow-[#FF6B35]/15"
          />
          <img
            data-testid="about-splash-image"
            src={media.splash}
            alt="Dynamic splash of juice and fruit"
            className="absolute -bottom-10 -right-8 hidden w-48 rounded-[2rem] shadow-xl md:block"
          />
        </div>
        <div>
          <p data-testid="about-overline" className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF4481]">
            About Lifestyle Juices
          </p>
          <h2 data-testid="about-title" className="mt-4 font-heading text-3xl font-semibold tracking-tight text-[#11221D] sm:text-4xl">
            A bright neighborhood stop for fresh flavor.
          </h2>
          <p data-testid="about-copy" className="mt-6 text-base leading-relaxed text-[#11221D]/75 md:text-lg">
            Lifestyle Juices brings made-to-order drinks to Des Moines with a fresh, colorful approach. Whether you want a quick wellness shot, a fruit-packed smoothie, or a clean juice, the experience is simple: real refreshment, served with local energy.
          </p>
          <div data-testid="offerings-list" className="mt-8 grid gap-3 sm:grid-cols-2">
            {business.offerings.map((offering) => (
              <div key={offering} data-testid={`offering-${offering.toLowerCase().replaceAll(" ", "-")}`} className="flex items-center gap-3 rounded-full bg-white px-4 py-3 shadow-lg shadow-[#96D035]/10">
                <span className="h-3 w-3 rounded-full bg-[#96D035]" aria-hidden="true" />
                <span className="font-bold text-[#11221D]">{offering}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ReviewsGallery() {
  return (
    <section id="reviews" data-testid="reviews-gallery-section" className="relative overflow-hidden px-6 py-20 md:px-12 lg:px-24 lg:py-32">
      <img src={media.abstract} alt="Soft tropical gradient" className="absolute inset-0 h-full w-full object-cover opacity-45" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl">
        <div className="max-w-3xl">
          <p data-testid="reviews-overline" className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF6B35]">
            Customer love
          </p>
          <h2 data-testid="reviews-title" className="mt-4 font-heading text-3xl font-semibold tracking-tight text-[#11221D] sm:text-4xl">
            The kind of spot people remember after one sip.
          </h2>
        </div>
        <div data-testid="testimonial-grid" className="mt-12 grid gap-6 lg:grid-cols-3">
          {testimonials.map((testimonial, index) => (
            <Card key={testimonial.name} data-testid={`testimonial-card-${index + 1}`} className="rounded-[2rem] border-white/70 bg-white/80 p-2 shadow-xl shadow-[#11221D]/5 backdrop-blur transition-transform hover:-translate-y-2">
              <CardContent className="p-6">
                <div className="flex gap-1 text-[#FF6B35]" data-testid={`testimonial-stars-${index + 1}`}>
                  {[...Array(5)].map((_, starIndex) => (
                    <Star key={starIndex} className="h-4 w-4 fill-current" aria-hidden="true" />
                  ))}
                </div>
                <p data-testid={`testimonial-quote-${index + 1}`} className="mt-5 text-lg font-bold leading-relaxed text-[#11221D]">
                  “{testimonial.quote}”
                </p>
                <p data-testid={`testimonial-name-${index + 1}`} className="mt-4 text-sm font-bold uppercase tracking-[0.18em] text-[#11221D]/45">
                  {testimonial.name}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div data-testid="gallery-grid" className="mt-12 grid gap-6 md:grid-cols-3">
          {media.gallery.map((image, index) => (
            <div key={image} data-testid={`gallery-image-card-${index + 1}`} className="group overflow-hidden rounded-[2rem] bg-white p-3 shadow-xl shadow-[#FF4481]/10">
              <img
                data-testid={`gallery-image-${index + 1}`}
                src={image}
                alt={`Lifestyle Juices gallery ${index + 1}`}
                className="aspect-[4/5] w-full rounded-[1.5rem] object-cover object-center transition-transform duration-500 group-hover:scale-105"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function VisitSection({ business }) {
  const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(business.address)}`;

  return (
    <section id="visit" data-testid="visit-section" className="bg-[#11221D] px-6 py-20 text-white md:px-12 lg:px-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-stretch">
        <div>
          <p data-testid="visit-overline" className="text-sm font-bold uppercase tracking-[0.2em] text-[#96D035]">
            Visit or call
          </p>
          <h2 data-testid="visit-title" className="mt-4 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
            Find Lifestyle Juices on University Ave.
          </h2>
          <p data-testid="visit-description" className="mt-6 text-base leading-relaxed text-white/70 md:text-lg">
            Stop by for fresh juices and smoothies, or call ahead to confirm today’s hours and current selections.
          </p>
          <div className="mt-8 space-y-4">
            <div data-testid="contact-address-card" className="rounded-[2rem] bg-white/10 p-5 backdrop-blur">
              <MapPin className="mb-3 h-6 w-6 text-[#FF6B35]" aria-hidden="true" />
              <p data-testid="contact-address" className="font-bold">{business.address}</p>
            </div>
            <div data-testid="contact-phone-card" className="rounded-[2rem] bg-white/10 p-5 backdrop-blur">
              <Phone className="mb-3 h-6 w-6 text-[#FF4481]" aria-hidden="true" />
              <p data-testid="contact-phone" className="font-bold">{business.phone}</p>
            </div>
            <div data-testid="contact-hours-card" className="rounded-[2rem] bg-white/10 p-5 backdrop-blur">
              <Clock className="mb-3 h-6 w-6 text-[#96D035]" aria-hidden="true" />
              <p data-testid="contact-hours" className="font-bold">{business.hours_note}</p>
              <div data-testid="weekly-hours-list" className="mt-4 grid gap-2">
                {(business.weekly_hours || []).map((row) => (
                  <div
                    data-testid={`weekly-hours-${row.days.toLowerCase().replaceAll("–", "-").replaceAll(" ", "-")}`}
                    key={row.days}
                    className="flex items-center justify-between gap-4 rounded-2xl bg-white/10 px-4 py-3"
                  >
                    <span>{row.days}</span>
                    <span>{row.hours}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-8 flex flex-col gap-4 sm:flex-row">
            <Button data-testid="visit-map-button" asChild className="rounded-full bg-[#96D035] px-7 py-6 text-base font-bold text-[#11221D] shadow-xl shadow-[#96D035]/20 transition-transform hover:-translate-y-1 hover:bg-[#B0E355]">
              <a href={mapUrl} target="_blank" rel="noreferrer">
                Get Directions <ArrowRight className="h-5 w-5" aria-hidden="true" />
              </a>
            </Button>
            <Button data-testid="visit-call-button" asChild className="rounded-full bg-[#FF4481] px-7 py-6 text-base font-bold text-white shadow-xl shadow-[#FF4481]/20 transition-transform hover:-translate-y-1 hover:bg-[#FF6E9D]">
              <a href={`tel:${business.phone.replace(/[^0-9]/g, "")}`}>Call the shop</a>
            </Button>
          </div>
        </div>
        <div data-testid="map-style-card" className="relative min-h-[420px] overflow-hidden rounded-[2.5rem] bg-[#F3F7F2] p-8 text-[#11221D] shadow-2xl shadow-black/20">
          <div className="absolute -right-14 -top-14 h-56 w-56 rounded-full bg-[#FF6B35]/30" aria-hidden="true" />
          <div className="absolute bottom-10 left-10 h-36 w-36 rounded-full bg-[#96D035]/50" aria-hidden="true" />
          <div className="relative z-10 flex h-full flex-col justify-between">
            <div>
              <Badge data-testid="map-card-badge" className="rounded-full border-0 bg-[#FF6B35] px-4 py-2 text-white">Des Moines, IA</Badge>
              <h3 data-testid="map-card-title" className="mt-8 font-heading text-4xl font-semibold">Fresh drinks are waiting.</h3>
            </div>
            <div data-testid="map-card-address-block" className="rounded-[2rem] bg-white p-6 shadow-xl shadow-[#11221D]/10">
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#FF4481]">Lifestyle Juices</p>
              <p className="mt-2 text-xl font-bold">{business.address}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer({ business }) {
  return (
    <footer data-testid="site-footer" className="bg-[#11221D] px-6 pb-10 text-white md:px-12 lg:px-24">
      <div className="mx-auto max-w-7xl border-t border-white/10 pt-10">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-center">
          <p data-testid="footer-tagline" className="max-w-xl text-white/65">
            Fresh juices, smoothies, and colorful wellness drinks made for your lifestyle.
          </p>
          <a data-testid="footer-phone-link" href={`tel:${business.phone.replace(/[^0-9]/g, "")}`} className="rounded-full bg-white px-5 py-3 font-bold text-[#11221D] transition-transform hover:-translate-y-1">
            {business.phone}
          </a>
        </div>
        <p data-testid="footer-brand-large" className="mt-12 font-heading text-5xl font-semibold tracking-tight text-white/15 sm:text-7xl lg:text-8xl">
          Lifestyle Juices
        </p>
      </div>
    </footer>
  );
}

function Home() {
  const [business, setBusiness] = useState(fallbackBusiness);

  useEffect(() => {
    const tagPlatformBadge = () => {
      const badgeLink = Array.from(document.querySelectorAll("a")).find((link) =>
        link.textContent?.includes("Made with Emergent")
      );

      if (badgeLink && !badgeLink.getAttribute("data-testid")) {
        badgeLink.setAttribute("data-testid", "platform-emergent-badge-link");
      }
    };

    tagPlatformBadge();
    const observer = new MutationObserver(tagPlatformBadge);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const loadBusiness = async () => {
      try {
        const response = await axios.get(`${API}/business`);
        setBusiness(response.data);
      } catch (error) {
        console.error("Unable to load business info; showing built-in content.", error);
      }
    };

    loadBusiness();
  }, []);

  return (
    <main data-testid="lifestyle-juices-page" className="min-h-screen overflow-x-hidden bg-[#FFFDFB] text-[#11221D]">
      <Header business={business} />
      <Hero business={business} />
      <MenuPreview business={business} />
      <AboutSection business={business} />
      <ReviewsGallery />
      <VisitSection business={business} />
      <Footer business={business} />
    </main>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;